import React, {useMemo} from 'react';
import {Redirect, Route, Switch} from 'react-router';

import NoMatch from '../components/NoMatch';
import {useStatsigGateWorkspaceConsolidation} from '../util/rampFeatureFlags';
import {makeRoutes} from './makeRoutes';
import {PrivateRoute} from './PrivateRoute';
import {RouteWithLayout} from './RouteWithLayout';

// When making changes to this file, please make sure to test routes and ensure performance does not regress.
// Changes to this file have a wide impact on the app and across multiple teams.
export const AppRoutes = React.memo(() => {
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  const filteredRoutes = useMemo(
    () =>
      makeRoutes(isWorkspaceConsolidationEnabled).filter(route =>
        'condition' in route ? route.condition !== false : true
      ),
    [isWorkspaceConsolidationEnabled]
  );

  return (
    <Switch>
      {filteredRoutes.map(route => {
        // Do not use the index as the key because this is NOT a static list of routes.
        // These are filtered down based on `condition`
        const key = Array.isArray(route.path)
          ? route.path.join('|')
          : route.path;

        if ('redirect' in route) {
          return <Redirect key={key} from={route.path} to={route.redirect} />;
        }

        const {RouteComponent} = route;

        return (
          <Route
            key={key}
            exact={route.exact}
            path={route.path}
            render={routeRenderProps => {
              // If we do not want to track this route, just render component
              if (route.skipTracking) {
                return (
                  <RouteComponent
                    {...routeRenderProps}
                    {...route.additionalProps}
                  />
                );
              }

              // Type guard to check if the route is private
              if ('isPrivateRoute' in route && route.isPrivateRoute === true) {
                return (
                  <PrivateRoute
                    additionalProps={route.additionalProps}
                    allowAnonymous={false}
                    allowIframes={route.additionalProps?.allowIframes}
                    component={RouteComponent}
                    match={routeRenderProps.match}
                    routeCache={route.additionalProps?.routeCache}
                    routeName={route.additionalProps?.routeName}
                  />
                );
              }

              // Type guard to check if the route is public
              if ('isPrivateRoute' in route && route.isPrivateRoute === false) {
                return (
                  <RouteWithLayout
                    additionalProps={route.additionalProps}
                    allowAnonymous={true}
                    allowIframes={route.additionalProps?.allowIframes}
                    component={RouteComponent}
                    match={routeRenderProps.match}
                    routeName={route.additionalProps?.routeName}
                  />
                );
              }

              // This should never happen. If it does, it's a bug.
              console.error('Invalid route configuration:', route);
              return null;
            }}
          />
        );
      })}
      <RouteWithLayout routeName="NoMatch" component={NoMatch} />
    </Switch>
  );
});
